import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, Eye, EyeOff, Calendar, MessageSquare, TrendingUp, Users, Award, BarChart3 } from "lucide-react";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { getMentorReviews, updateReviewVisibility, getMentorRatingStats, type MentorReview } from "@/services/mentorReviewService";
import { format, parseISO } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from 'sonner';

interface RatingStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: { [key: number]: number };
}

const MinhasAvaliacoes: React.FC = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<MentorReview[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = async () => {
    if (!user?.id) {
      console.log('❌ [MinhasAvaliacoes] Usuário não encontrado');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 [MinhasAvaliacoes] Buscando avaliações para mentor:', user.id);
      
      // Primeiro, vamos tentar uma query simples para verificar se a tabela existe
      const { data: testData, error: testError } = await supabase
        .from('session_reviews')
        .select('count')
        .eq('mentor_id', user.id);
      
      console.log('🧪 [MinhasAvaliacoes] Teste de conexão:', { testData, testError });
      
      if (testError) {
        console.error('❌ [MinhasAvaliacoes] Erro no teste de conexão:', testError);
        throw new Error(`Erro de conexão: ${testError.message}`);
      }

      // Agora buscar as avaliações usando o serviço corrigido
      const reviewsData = await getMentorReviews(user.id);
      console.log('✅ [MinhasAvaliacoes] Avaliações carregadas:', reviewsData);
      
      const statsData = await getMentorRatingStats(user.id);
      console.log('📊 [MinhasAvaliacoes] Estatísticas carregadas:', statsData);
      
      setReviews(reviewsData);
      setStats(statsData);
    } catch (error: any) {
      console.error('❌ [MinhasAvaliacoes] Erro ao carregar avaliações:', error);
      setError(error.message || 'Erro ao carregar avaliações');
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (reviewId: string, currentVisibility: boolean) => {
    try {
      await updateReviewVisibility(reviewId, !currentVisibility);
      
      // Atualizar estado local
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, is_public: !currentVisibility }
          : review
      ));

      toast.success(
        !currentVisibility 
          ? 'Avaliação tornada pública' 
          : 'Avaliação tornada privada'
      );
    } catch (error) {
      console.error('Error updating review visibility:', error);
      toast.error('Erro ao atualizar visibilidade da avaliação');
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5'
    };

    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`${sizeClasses[size]} ${
              i < rating
                ? "text-amber-400 fill-amber-400"
                : "text-gray-600 fill-gray-600"
            }`}
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    fetchReviews();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131922]">
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="space-y-4">
              <Skeleton className="h-10 w-80 bg-[#1A2233]" />
              <Skeleton className="h-6 w-96 bg-[#1A2233]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Skeleton className="h-40 bg-[#1A2233]" />
              <Skeleton className="h-40 bg-[#1A2233]" />
              <Skeleton className="h-40 bg-[#1A2233]" />
            </div>
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-48 bg-[#1A2233]" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#131922]">
        <div className="container mx-auto px-4 py-8">
          <Card className="border-red-500/30 bg-[#1A2233] shadow-lg">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-red-400 mb-3">
                  Erro ao carregar avaliações
                </h3>
                <p className="text-red-300 mb-6 max-w-md mx-auto">{error}</p>
                <Button 
                  onClick={fetchReviews} 
                  className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
                >
                  Tentar novamente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#131922]">
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Header com gradiente */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1A2233] via-[#223342] to-[#2A3749] p-8 text-white shadow-2xl border border-[#2D3748]">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-[#00B6F0]/20 rounded-xl backdrop-blur-sm">
                  <Award className="w-8 h-8 text-[#00B6F0]" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white">Minhas Avaliações</h1>
                  <p className="text-gray-300 text-lg mt-1">
                    Gerencie o feedback dos seus jogadores
                  </p>
                </div>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#00B6F0]/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#00B6F0]/5 rounded-full blur-2xl"></div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-[#1A2233] border-[#2D3748] shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-400 mb-1">Nota Média</p>
                      <p className="text-3xl font-bold text-white">
                        {stats.averageRating.toFixed(1)}
                      </p>
                      <div className="mt-2">
                        {renderStars(Math.round(stats.averageRating), 'sm')}
                      </div>
                    </div>
                    <div className="p-4 bg-[#00B6F0]/20 rounded-xl">
                      <TrendingUp className="w-8 h-8 text-[#00B6F0]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A2233] border-[#2D3748] shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-400 mb-1">Total de Avaliações</p>
                      <p className="text-3xl font-bold text-white">{stats.totalReviews}</p>
                      <p className="text-xs text-gray-500 mt-1">Feedback recebido</p>
                    </div>
                    <div className="p-4 bg-green-500/20 rounded-xl">
                      <Users className="w-8 h-8 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1A2233] border-[#2D3748] shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-400 mb-1">Avaliações Públicas</p>
                      <p className="text-3xl font-bold text-white">
                        {reviews.filter(r => r.is_public).length}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Visíveis no perfil</p>
                    </div>
                    <div className="p-4 bg-purple-500/20 rounded-xl">
                      <Eye className="w-8 h-8 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Rating Distribution */}
          {stats && stats.totalReviews > 0 && (
            <Card className="bg-[#1A2233] border-[#2D3748] shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-white">
                  <div className="p-2 bg-[#2D3748] rounded-lg">
                    <BarChart3 className="w-5 h-5 text-[#00B6F0]" />
                  </div>
                  Distribuição das Notas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = stats.ratingDistribution[rating] || 0;
                    const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
                    
                    return (
                      <div key={rating} className="flex items-center gap-4">
                        <div className="flex items-center gap-2 w-20">
                          <span className="text-sm font-semibold text-gray-300 w-3">{rating}</span>
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        </div>
                        <div className="flex-1 bg-[#2D3748] rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-[#00B6F0] to-[#0099CC] h-3 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="flex items-center gap-2 w-16">
                          <span className="text-sm font-medium text-gray-300">{count}</span>
                          <span className="text-xs text-gray-500">({percentage.toFixed(0)}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews List */}
          <Card className="bg-[#1A2233] border-[#2D3748] shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-white">
                <div className="p-2 bg-[#2D3748] rounded-lg">
                  <MessageSquare className="w-5 h-5 text-[#00B6F0]" />
                </div>
                Todas as Avaliações
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[60vh] overflow-y-auto">
              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#2D3748] rounded-full flex items-center justify-center mx-auto mb-6">
                    <MessageSquare className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Nenhuma avaliação ainda
                  </h3>
                  <p className="text-gray-400 max-w-md mx-auto">
                    Suas avaliações aparecerão aqui quando os jogadores avaliarem suas mentorias.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="group relative bg-[#131922] border border-[#2D3748] rounded-xl p-6 hover:shadow-lg hover:border-[#00B6F0]/50 transition-all duration-300"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <Avatar className="w-12 h-12 ring-2 ring-[#2D3748] ring-offset-2 ring-offset-[#131922]">
                            <AvatarImage 
                              src={review.player_profile?.image_url} 
                              alt={review.player_profile?.nickname || 'Jogador'} 
                            />
                            <AvatarFallback className="bg-gradient-to-br from-[#00B6F0] to-[#0099CC] text-black font-semibold">
                              {(review.player_profile?.nickname || 'J').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-white truncate">
                                {review.player_profile?.nickname || 'Jogador Anônimo'}
                              </h4>
                              {renderStars(review.rating, 'sm')}
                              <Badge 
                                variant={review.is_public ? 'default' : 'secondary'}
                                className={review.is_public 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                }
                              >
                                {review.is_public ? 'Pública' : 'Privada'}
                              </Badge>
                            </div>
                            
                            <p className="text-gray-300 mb-3 leading-relaxed">{review.comment}</p>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(review.created_at).toLocaleDateString('pt-BR')}
                              </span>
                              {review.session?.mentorship_offerings?.title && (
                                <span className="flex items-center gap-1">
                                  <span>•</span>
                                  <span className="truncate">{review.session.mentorship_offerings.title}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleVisibility(review.id, review.is_public)}
                          className={`shrink-0 transition-all duration-200 ${
                            review.is_public 
                              ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:border-orange-500/50' 
                              : 'border-green-500/30 text-green-400 hover:bg-green-500/10 hover:border-green-500/50'
                          }`}
                        >
                          {review.is_public ? (
                            <>
                              <EyeOff className="w-4 h-4 mr-2" />
                              Tornar Privada
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 w-4 mr-2" />
                              Tornar Pública
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MinhasAvaliacoes; 